import React from 'react';
import { motion } from 'framer-motion';
import { DatabaseIcon, SettingsIcon, BuildingIcon, MessageCircleIcon, CalendarIcon, MapPinIcon, PhoneIcon, MailIcon, LinkedinIcon, InstagramIcon, FacebookIcon, ChevronRightIcon, LayoutGridIcon } from 'lucide-react';
import { sectorPages } from '../data/sectorPages';
import { werkgebiedPlaces } from '../data/werkgebied';
const footerLinks = {
  diensten: {
    title: 'Diensten',
    links: [{
      label: 'Websites',
      href: '/websites'
    }, {
      label: 'Digitale vormgeving',
      href: '/vormgeving'
    }, {
      label: 'Webapplicaties',
      href: '/webapplicatie-laten-maken'
    }, {
      label: 'Hosting',
      href: '/hosting'
    }, {
      label: 'E-mail',
      href: '/email'
    }, {
      label: 'SEO',
      href: '/seo'
    }, {
      label: 'CorePrint',
      href: 'https://coreprint.nl/',
      external: true
    }]
  },
  noordDigital: {
    title: 'Noord Digital',
    links: [{
      label: 'Over ons',
      href: '/over-ons'
    }, {
      label: 'Ons team',
      href: '/over-ons#team'
    }, {
      label: 'Klantcases',
      href: '/#client-cases'
    }, {
      label: 'Kennisbank',
      href: '/bibliotheek'
    }, {
      label: 'Vacatures',
      href: '/sollicitatie'
    }]
  }
};
// De eerste vijf sectorpagina's krijgen een plek in de kolom "Voor wie".
// Alle sectoren staan daarnaast in de strook onderaan, zodat elke pagina
// van de site naar alle sectorpagina's linkt.
const voorWieLinks = sectorPages.slice(0, 5);
const socialLinks = [{
  icon: LinkedinIcon,
  href: 'https://www.linkedin.com/company/noord-digital/',
  label: 'LinkedIn'
}, {
  icon: FacebookIcon,
  href: 'https://www.facebook.com/profile.php?id=61572493568612',
  label: 'Facebook'
}, {
  icon: InstagramIcon,
  href: 'https://www.instagram.com/noord_digital/',
  label: 'Instagram'
}];
const fadeUp = {
  initial: {
    opacity: 0,
    y: 16
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  }
};
export function Footer() {
  const jaar = new Date().getFullYear();
  return <footer className="w-full bg-white relative pt-[480px] sm:pt-[420px] md:pt-56 mt-16" data-id="element-351" data-mp="yu7ictvibtz9">
      {/* Top Curved Banner */}
      <div className="absolute top-0 left-0 w-full -mt-8 px-4 md:px-6 lg:px-8 z-20" data-id="element-352" data-mp="13t0v2k2yzgyd">
        <div className="container mx-auto max-w-[1200px]" data-id="element-353" data-mp="18jr2e2uyhtvp">
          <div className="relative rounded-[2.5rem] overflow-hidden bg-[#007AFF] shadow-2xl flex flex-col md:flex-row items-stretch min-h-[320px]" data-id="element-354" data-mp="18lbxpup3hgum">
            {/* Blue Background with Curve */}
            <div className="absolute inset-0 bg-[#0066CC] z-0" style={{
            clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 85%)'
          }} data-id="element-355" data-mp="1hiulh6u4i9a7"></div>
            <div className="absolute inset-0 bg-[#007AFF] z-0" style={{
            clipPath: 'polygon(0 0, 80% 0, 60% 100%, 0 100%)'
          }} data-id="element-356" data-mp="1rj4yaqc8xeou"></div>

            {/* Content Left */}
            <div className="relative z-10 p-10 md:p-16 md:w-[60%] flex flex-col items-start justify-center" data-id="element-357" data-mp="3t9ylnzsh707e">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-8 leading-tight max-w-lg" data-id="element-358" data-mp="2afn76wxgt9sw">
                Jouw bedrijf een boost geven?
              </h2>
              <a href="#contact" className="inline-flex items-center bg-white text-[#007AFF] font-bold py-4 px-8 rounded-full hover:bg-gray-50 transition-colors shadow-md" data-id="element-359" data-mp="21mtjcb3yd23f">
                Neem contact met ons op{' '}
                <ChevronRightIcon className="ml-2 w-5 h-5" data-id="element-360" data-mp="2tehn498o0gan" />
              </a>
            </div>

            {/* Image Right */}
            <div className="relative z-10 w-full md:w-[50%] h-64 md:h-auto md:absolute md:right-0 md:top-0 md:bottom-0" data-id="element-361" data-mp="2pdpf1jbj4hfy">
              <div className="absolute inset-0 bg-gradient-to-r from-[#007AFF] to-transparent z-10 hidden md:block w-32" data-id="element-362" data-mp="2kfe6dbwz0czb"></div>
              <img src="https://cdn.magicpatterns.com/uploads/kPiXVykaBQH5ZZ9eYcbZFN/image.png" alt="Team overleg Noord Digital" loading="lazy" className="absolute inset-0 w-full h-full object-cover object-center" style={{
              clipPath: 'polygon(15% 0, 100% 0, 100% 100%, 0 100%)'
            }} data-id="element-363" data-mp="18y4kyjua7avd" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] pt-48 pb-12" data-id="element-364" data-mp="2iv1ccdhur22o">
        {/* Top row — Logo + tagline + socials */}
        <motion.div {...fadeUp} className="flex flex-col md:flex-row md:items-start md:justify-between gap-8 mb-16 pb-12 border-b border-gray-100" data-id="element-365" data-mp="2nuxkmoxl1fo1">
          <div className="flex items-start gap-4 max-w-md" data-id="element-366" data-mp="3flbrw2b3btqq">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/mbRatHaTnCE2T37GsGP7pj/Norden_portret_300dpi.png" alt="Noord Digital logo" loading="lazy" className="w-14 h-14 object-contain flex-shrink-0 -mt-1" data-id="element-367" data-mp="3fks9kkhj33c6" />
            <div data-id="element-368" data-mp="36mqe0kgkd2wa">
              <h3 className="text-2xl font-bold tracking-tight mb-1 text-[#111827]" data-id="element-369" data-mp="1q6doca5pxylf">
                Noord Digital
              </h3>
              <p className="text-[#004AC7] font-semibold text-sm mb-2" data-id="element-370" data-mp="1v5qjrjanodzm">
                Online groei op basis van bewijs.
              </p>
              <p className="text-[#6B7280] text-sm leading-relaxed" data-id="element-371" data-mp="1nk4ox2x6nmsr">
                Wij zorgen dat ondernemers in Noord-Holland Noord online
                gevonden worden en professioneel overkomen. Met data als
                fundament, niet als bijzaak.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 md:pt-1" data-id="element-372" data-mp="1rlq5ozcu8l88">
            {socialLinks.map(social => <a key={social.label} href={social.href} target="_blank" rel="noopener noreferrer" aria-label={`Noord Digital op ${social.label}`} className="w-10 h-10 rounded-full bg-[#F7F8FA] flex items-center justify-center text-[#6B7280] hover:bg-[#004AC7] hover:text-white transition-all duration-200" data-id="element-373" data-mp="13vvuem8634q3">
                <social.icon className="w-4 h-4" data-id="element-374" data-mp="18uyrnkhwo6ni" />
              </a>)}
          </div>
        </motion.div>

        {/* Navigation columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-16" data-id="element-375" data-mp="g8xdxok1rws6">
          {/* Diensten */}
          <motion.div {...fadeUp} transition={{
          delay: 0.05
        }} data-id="element-376" data-mp="2g9ly1zvm54jl">
            <div className="flex items-center gap-2 mb-5" data-id="element-377" data-mp="2l96ti0bkqurk">
              <SettingsIcon className="w-4 h-4 text-[#004AC7]" data-id="element-378" data-mp="2l7niao4rz9wn" />
              <h4 className="text-[#111827] font-semibold text-sm uppercase tracking-wider" data-id="element-379" data-mp="2q1rlqxeu0zsz">
                {footerLinks.diensten.title}
              </h4>
            </div>
            <ul className="space-y-3" data-id="element-380" data-mp="1xcb1wo5a2r8d">
              {footerLinks.diensten.links.map(link => <li key={link.label} data-id="element-381" data-mp="22am88owd6jlu">
                  <a href={link.href} target={link.external ? '_blank' : undefined} rel={link.external ? 'noopener noreferrer' : undefined} className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200 flex items-center gap-2" data-id="element-382" data-mp="22bnk2vl7fa4m">
                    <ChevronRightIcon className="w-3 h-3 text-gray-300" data-id="element-383" data-mp="3ip4939sthbnc" />
                    {link.label}
                  </a>
                </li>)}
            </ul>
          </motion.div>

          {/* Voor wie — nu de echte sectorpagina's */}
          <motion.div {...fadeUp} transition={{
          delay: 0.1
        }} data-id="element-384" data-mp="1b2azxkncvpf5">
            <div className="flex items-center gap-2 mb-5" data-id="element-385" data-mp="171imef7gayd2">
              <DatabaseIcon className="w-4 h-4 text-[#004AC7]" data-id="element-386" data-mp="1744ucfy97fvt" />
              <h4 className="text-[#111827] font-semibold text-sm uppercase tracking-wider" data-id="element-387" data-mp="129r38pqmjuwy">
                Voor wie
              </h4>
            </div>
            <ul className="space-y-3" data-id="element-388" data-mp="scmimucfg7i9">
              {voorWieLinks.map(p => <li key={p.slug} data-id="element-389" data-mp="se5zon5wqml3">
                  <a href={`/website-voor/${p.slug}`} className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200 flex items-center gap-2" data-id="element-390" data-mp="ne1ggpdm7dr6">
                    <ChevronRightIcon className="w-3 h-3 text-gray-300 flex-shrink-0" data-id="element-391" data-mp="jeahhydxprbk" />
                    {p.sector}
                  </a>
                </li>)}
              <li className="pt-1" data-id="element-392" data-mp="2dp61dwukkstn">
                <a href="https://webdesignalkmaar.nl/" target="_blank" rel="noopener noreferrer" className="text-[#9CA3AF] hover:text-[#004AC7] text-xs transition-colors duration-200 flex items-center gap-2" data-id="element-393" data-mp="2do4qbsdk06bx">
                  <ChevronRightIcon className="w-3 h-3 text-gray-200" data-id="element-394" data-mp="1ho4k8sk1qkkg" />
                  Webdesign Alkmaar
                </a>
              </li>
            </ul>
          </motion.div>

          {/* Noord Digital */}
          <motion.div {...fadeUp} transition={{
          delay: 0.15
        }} data-id="element-395" data-mp="1cv1xp4dosbdr">
            <div className="flex items-center gap-2 mb-5" data-id="element-396" data-mp="1csp8c65lmyqo">
              <BuildingIcon className="w-4 h-4 text-[#004AC7]" data-id="element-397" data-mp="yuhhmfz5otny" />
              <h4 className="text-[#111827] font-semibold text-sm uppercase tracking-wider" data-id="element-398" data-mp="12wm5sz6u4y37">
                {footerLinks.noordDigital.title}
              </h4>
            </div>
            <ul className="space-y-3" data-id="element-399" data-mp="ozfpdb9l3bh5">
              {footerLinks.noordDigital.links.map(link => <li key={link.label} data-id="element-400" data-mp="tya9cgjaf62u">
                  <a href={link.href} className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200 flex items-center gap-2" data-id="element-401" data-mp="2jam6wdhf2ts1">
                    <ChevronRightIcon className="w-3 h-3 text-gray-300" data-id="element-402" data-mp="2o9fjm3ovqgr5" />
                    {link.label}
                  </a>
                </li>)}
            </ul>
          </motion.div>

          {/* Contact */}
          <motion.div {...fadeUp} transition={{
          delay: 0.2
        }} data-id="element-403" data-mp="7lo43plicax7">
            <div className="flex items-center gap-2 mb-5" data-id="element-404" data-mp="mdzmkc6am8sw">
              <MessageCircleIcon className="w-4 h-4 text-[#004AC7]" data-id="element-405" data-mp="hlomn9bjyn0c" />
              <h4 className="text-[#111827] font-semibold text-sm uppercase tracking-wider" data-id="element-406" data-mp="vdcx7865l3f7">
                {footerLinks.contact?.title ?? 'Contact'}
              </h4>
            </div>
            <address className="not-italic" data-id="element-407" data-mp="qgmj2qdwshwf">
              <ul className="space-y-3" data-id="element-408" data-mp="15bsobb56kcop">
                <li className="text-[#111827] text-sm font-semibold" data-id="element-409" data-mp="159gb1badaqhz">
                  Noord Digital
                </li>
                <li data-id="element-410" data-mp="372w56i5nynhp">
                  <a href="/#contact" className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200 flex items-center gap-2" data-id="element-411" data-mp="372e0cx4xr0y6">
                    <CalendarIcon className="w-3.5 h-3.5 text-[#004AC7]" data-id="element-412" data-mp="325vws0lcd8nf" />
                    Kennismaking plannen
                  </a>
                </li>
                <li data-id="element-413" data-mp="duvwq1k5w3rm">
                  <a href="https://maps.google.com/?q=Schuit+8a+1724+BD+Oudkarspel" target="_blank" rel="noopener noreferrer" className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200 flex items-start gap-2" data-id="element-414" data-mp="dtkl5fkh5rpy">
                    <MapPinIcon className="w-3.5 h-3.5 text-[#004AC7] mt-0.5 flex-shrink-0" data-id="element-415" data-mp="11kqbioo1k2ei" />
                    <span data-id="element-416" data-mp="11mkl56tv483a">
                      Schuit 8a, 1724 BD Oudkarspel
                      <span className="block text-xs text-[#6B7280] mt-0.5" data-id="element-417" data-mp="wmezc7a9xft4">
                        Gemeente Dijk en Waard
                      </span>
                    </span>
                  </a>
                </li>
                <li data-id="element-418" data-mp="mtzgz8v2ep7p">
                  <a href="tel:+31653689686" className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200 flex items-center gap-2" data-id="element-419" data-mp="1kbr8561ovluw">
                    <PhoneIcon className="w-3.5 h-3.5 text-[#004AC7]" data-id="element-420" data-mp="1fiekoz8qxl3q" />
                    06 53689686
                  </a>
                </li>
                <li data-id="element-421" data-mp="1ru78frnrkiyn">
                  <a href="mailto:info@noorddigital.nl" className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200 flex items-center gap-2" data-id="element-422" data-mp="1rvs5bnxjwdzo">
                    <MailIcon className="w-3.5 h-3.5 text-[#004AC7]" data-id="element-423" data-mp="21smx7nzbjg4z" />
                    info@noorddigital.nl
                  </a>
                </li>
                <li className="text-[#6B7280] text-xs pl-[1.375rem]" data-id="element-424" data-mp="26rr8ck5sznxy">
                  KvK 96071044
                </li>
              </ul>
            </address>
          </motion.div>
        </div>

        {/* Websites per branche — alle sectorpagina's, op elke pagina van de site.
            Dit geeft de sectorpagina's interne links vanaf de hele website. */}
        <motion.nav {...fadeUp} transition={{
        delay: 0.22
      }} aria-label="Websites per branche" className="border-t border-gray-100 pt-10 mb-12" data-id="element-425" data-mp="2pjac4t7nj611">
          <div className="flex items-center gap-2 mb-5" data-id="element-426" data-mp="2uf7okofsns8j">
            <LayoutGridIcon className="w-4 h-4 text-[#004AC7]" data-id="element-427" data-mp="24u81ng5htvv8" />
            <h4 className="text-[#111827] font-semibold text-sm uppercase tracking-wider" data-id="element-428" data-mp="1zuveeuapmms4">
              Website laten maken voor jouw branche
            </h4>
          </div>
          <ul className="flex flex-wrap gap-x-6 gap-y-3" data-id="element-429" data-mp="2el2ksbng0fcg">
            {sectorPages.map(p => <li key={p.slug} data-id="element-430" data-mp="29qgakbt3ksty">
                <a href={`/website-voor/${p.slug}`} className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200" data-id="element-431" data-mp="2njo4490qnyea">
                  {p.sector}
                </a>
              </li>)}
          </ul>
        </motion.nav>

        {/* Werkgebied: interne links naar alle plaatspagina's */}
        <motion.nav {...fadeUp} transition={{
        delay: 0.24
      }} aria-label="Werkgebied" className="border-t border-gray-100 pt-10 mb-12" data-id="element-432" data-mp="2sihj65vobxgk">
          <div className="flex items-center gap-2 mb-5" data-id="element-433" data-mp="134cnji0zyxuz">
            <MapPinIcon className="w-4 h-4 text-[#004AC7]" data-id="element-434" data-mp="y5rovn1e8m8n" />
            <h4 className="text-[#111827] font-semibold text-sm uppercase tracking-wider" data-id="element-435" data-mp="y7cj1bjsq35z">
              <a href="/werkgebied" className="hover:text-[#004AC7] transition-colors duration-200" data-id="element-436" data-mp="39ufy6dd1uft1">
                Werkgebied
              </a>
            </h4>
          </div>
          <ul className="flex flex-wrap gap-x-6 gap-y-3" data-id="element-437" data-mp="3020gzi9ktv97">
            {werkgebiedPlaces.map(place => <li key={place.slug} data-id="element-438" data-mp="350bpnpo4xzpu">
                <a href={`/werkgebied/${place.slug}`} className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200" data-id="element-439" data-mp="34za58ul8c0vg">
                  {place.name}
                </a>
              </li>)}
          </ul>
        </motion.nav>

        {/* Bottom bar */}
        <motion.div {...fadeUp} transition={{
        delay: 0.25
      }} className="border-t border-gray-200 pt-8 flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-440" data-mp="2h5iimi082x3z">
          <div className="flex items-center gap-3" data-id="element-441" data-mp="fdm89bpsjtas">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/mbRatHaTnCE2T37GsGP7pj/Norden_portret_300dpi.png" alt="Noord Digital" loading="lazy" className="w-8 h-8 object-contain flex-shrink-0" data-id="element-442" data-mp="kbdxfxz1x1ba" />
            <div className="text-center md:text-left" data-id="element-443" data-mp="2bfpbdl6vjql5">
              <p className="text-[#6B7280] text-sm" data-id="element-444" data-mp="2bfz5nkyycjwn">
                © {jaar} Noord Digital. Alle rechten voorbehouden.
              </p>
              {/* Wettelijk verplichte bedrijfsgegevens */}
              <p className="text-[#9CA3AF] text-sm mt-1" data-id="element-445" data-mp="22e9emg8zfmze">
                KvK 96071044 · BTW-id NL867457521B01
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap justify-center" data-id="element-446" data-mp="1sopruzi5f8z9">
            <a href="/privacy" className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200" data-id="element-447" data-mp="1npv1nchuqi54">
              Privacybeleid
            </a>
            <span className="text-gray-300" data-id="element-448" data-mp="1jq4dlgjpmg4a">·</span>
            <a href="/algemene-voorwaarden" className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200" data-id="element-449" data-mp="1epq2fpmtaq21">
              Algemene voorwaarden
            </a>
            <span className="text-gray-300" data-id="element-450" data-mp="3ldpdugbwtx65">·</span>
            <a href="/cookiebeleid" className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200" data-id="element-451" data-mp="3lcxgp12nzrdr">
              Cookiebeleid
            </a>
            <span className="text-gray-300" data-id="element-452" data-mp="3vbd8z7dfh6ou">·</span>
            <a href="/sitemap" className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200" data-id="element-453" data-mp="3v9ilblarv0h2">
              Sitemap
            </a>
            <span className="text-gray-300" data-id="element-454" data-mp="32o0msyb6j0w2">·</span>
            <button type="button" onClick={() => {
            window.dispatchEvent(new CustomEvent('nd-open-cookie-settings'));
          }} className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200" data-id="element-455" data-mp="32l4ov9zc0c7m">
              Cookie-instellingen
            </button>
          </div>
        </motion.div>
      </div>
    </footer>;
}